import { Injectable, inject, signal } from '@angular/core';
import { EwentsToastService } from '@ewentscompany/ewents-components';
import { Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';

import { PermissionUtils } from '../shared/utils/permission.utils';
import { showErrorSnackBar } from '../shared/utils/snackbar.utils';
import { AssignmentService } from './assignment.service';

/** Shared apply + pending-count indicator for down-payment invoices (FINANCIAL.MANAGER). */
@Injectable({ providedIn: 'root' })
export class AssignmentDownPaymentService {
  private readonly assignmentService = inject(AssignmentService);
  private readonly toast = inject(EwentsToastService);
  private readonly permissionUtils = inject(PermissionUtils);

  readonly pendingCount = signal(0);

  refreshPendingCount(): void {
    if (!this.permissionUtils.canApplyDownPayment()) {
      this.pendingCount.set(0);
      return;
    }

    this.countAllPending().subscribe({
      next: count => this.pendingCount.set(count),
      error: () => this.pendingCount.set(0),
    });
  }

  async applyDownPayment(assignmentId: number): Promise<boolean> {
    if (!this.permissionUtils.canApplyDownPayment()) {
      this.toast.warning('You do not have permission to apply down payments');
      return false;
    }

    try {
      await firstValueFrom(this.assignmentService.applyDownPayment(assignmentId));
      this.toast.success('Down payment applied successfully! Invoice generated.');
      this.refreshPendingCount();
      return true;
    } catch (error) {
      console.error('Error applying down payment:', error);
      showErrorSnackBar(this.toast, error, { defaultMessage: 'Failed to apply down payment' });
      return false;
    }
  }

  private countAllPending(): Observable<number> {
    return this.assignmentService
      .getAssignmentsPaginated(1, 1, undefined, { pendingDownPayment: true })
      .pipe(map(res => res.total));
  }
}
